import React, { forwardRef } from 'react';
import './PriceBreakUp.scss';

const _discountType = ['gv', 'sc', 'prepaid', 'coupon', 'lenskart', 'lenskartplus'];

function getNetAmount(subTotal, preTaxDiscount) {
  const amount = subTotal - preTaxDiscount;
  if (!Number.isInteger(amount)) {
    return amount.toFixed(2);
  }
  return amount;
}

function PriceBreakUp(props, ref) {
  const {
    view,
    currencyMark,
    amount: { currencyCode = 'INR', subTotal, total, totalTax, shipping, discounts = [] },
    dataLocale,
  } = props;
  let oldOrderDiscount = 0;
  let scDiscount = 0;
  let preTaxDiscount = 0;
  let prepaidDiscount = 0;
  const currencySymbol = currencyCode ? (
    <span>{currencyMark[currencyCode]}</span>
  ) : (
    <span>{currencyMark[dataLocale.CURRENCY_CODE]}</span>
  );
  const {
    CONVENIENCE_FEES,
    NET_AMOUNT,
    STORE_CREDIT,
    TOTAL,
    OLD_PRODUCT_EXCHANGE_VALUE,
    GST_COLLECTED,
    PREPAID_DISCOUNT,
    PRICE_BREAKUP,
    VOUCHER_DISCOUNT,
    SUBTOTAL,
    LK_CASH,
    LK_CASH_PLUS,
    COUPON_DISCOUNT,
    AUTO_DISCOUNT,
  } = dataLocale;

  return (
    <div
      ref={ref}
      className={`order-price ${
        view === 'desktop'
          ? 'desktop flex-basis-88'
          : 'pd-l15 pd-r15 pd-t20 pd-b70 bg-color_off_white'
      }`}
      data-testid="priceBreakUp"
    >
      <div
        className={`${
          view === 'desktop'
            ? 'font-bold text-color-33 fs14 margin-b20'
            : 'fw700 fsp16 mr-b20 text-color_grey_black'
        }`}
      >
        {PRICE_BREAKUP}:
      </div>
      <div
        className={`${view === 'desktop' ? 'text-color-grey fs14' : 'text-color_grey_black fsp14'}`}
      >
        <span>{SUBTOTAL}</span>
        <span className="pull-right">
          {currencySymbol} {subTotal}
        </span>
      </div>
      {discounts.map((discount, index) => {
        const { type, amount } = discount;
        if (amount > 0) {
          if (type === 'sc') {
            scDiscount = amount;
            return null;
          } else if (type === 'prepaid') {
            prepaidDiscount = amount;
            return null;
          } else if (type === 'exchange') {
            oldOrderDiscount = amount;
            return null;
          }
          preTaxDiscount += amount;
          return (
            <div
              key={index}
              className={`${
                view === 'desktop'
                  ? 'text-color-grey fs14 margin-t4'
                  : 'mr-t4 text-color_grey_black fsp14'
              }`}
            >
              <span>
                {
                  {
                    gv: VOUCHER_DISCOUNT,
                    coupon: COUPON_DISCOUNT,
                    lenskart: LK_CASH,
                    lenskartplus: LK_CASH_PLUS,
                  }[type]
                }
                {!_discountType.includes(type) && AUTO_DISCOUNT}
              </span>
              <span className="pull-right">
                - {currencySymbol} {amount}
              </span>
            </div>
          );
        }
        return null;
      })}
      <div
        className={`net-amount ${
          view === 'desktop'
            ? 'margin-t12 margin-b10 padding-t12 padding-b12'
            : 'mr-t12 mr-b10 pd-t12 pd-b12'
        }`}
      >
        <div
          className={`fw700 ${
            view === 'desktop'
              ? 'text-color-33 fs14 margin-b10'
              : 'text-color_grey_black fsp14 mr-b10'
          }`}
        >
          <span>{NET_AMOUNT}</span>
          <span className="pull-right">
            {currencySymbol} {getNetAmount(subTotal, preTaxDiscount)}
          </span>
        </div>
        {totalTax > 0 && (
          <div
            className={`${
              view === 'desktop'
                ? 'fs12 margin-b4 text-color-grey'
                : 'text-color_dull_black fsp12 mr-b4'
            }`}
          >
            <span>{GST_COLLECTED}</span>
            <span className="pull-right">
              {totalTax > 0 ? '+' : ''} {currencySymbol} {totalTax}
            </span>
          </div>
        )}
        {prepaidDiscount > 0 && (
          <div
            className={`${
              view === 'desktop'
                ? 'fs12 margin-b4 text-color-grey'
                : 'text-color_dull_black fsp12 mr-b4'
            }`}
          >
            <span>{PREPAID_DISCOUNT}</span>
            <span className="pull-right">
              - {currencySymbol} {prepaidDiscount}
            </span>
          </div>
        )}
        {oldOrderDiscount > 0 && (
          <div
            className={`${
              view === 'desktop'
                ? 'fs12 margin-b4 text-color-grey'
                : 'text-color_dull_black fsp12 mr-b4'
            }`}
            data-testid="oldOrderDiscount"
          >
            <span>{OLD_PRODUCT_EXCHANGE_VALUE}</span>
            <span className="pull-right">
              - {currencySymbol} {oldOrderDiscount}
            </span>
          </div>
        )}
        {scDiscount > 0 && (
          <div
            className={`${
              view === 'desktop'
                ? 'fs12 margin-b4 text-color-grey'
                : 'text-color_dull_black fsp12 mr-b4'
            }`}
          >
            <span>{STORE_CREDIT}</span>
            <span className="pull-right">
              - {currencySymbol} {scDiscount}
            </span>
          </div>
        )}
        <div
          className={`${
            view === 'desktop'
              ? 'fs12 margin-b4 text-color-grey'
              : 'text-color_dull_black fsp12 mr-b4'
          }`}
        >
          <span>{CONVENIENCE_FEES}</span>
          <span className="pull-right">
            {shipping > 0 ? (
              <span>
                + {currencySymbol} {shipping}
              </span>
            ) : (
              'Free'
            )}
          </span>
        </div>
      </div>

      <div
        className={`fw700 ${
          view === 'desktop' ? 'fs14 margin-b4 text-color-33' : 'text-color_grey_black fsp14 mr-b4'
        }`}
      >
        <span>{TOTAL}</span>
        <span className="pull-right" data-testid="orderTotal">
          {currencySymbol} {total}
        </span>
      </div>
    </div>
  );
}

export default forwardRef(PriceBreakUp);
